import React, { useState, useEffect } from 'react';
import { Container, Grid, Card, CardContent, Typography, Box, Tab, Tabs, Button, Alert } from '@mui/material';
import { styled } from '@mui/material/styles';
import TourismInputForm from '../../components/tourism/TourismInputForm';
import TourismCalculationResults from '../../components/tourism/TourismCalculationResults';
import TourismCalculationHistory from '../../components/tourism/TourismCalculationHistory';
import TourismComparisonView from '../../components/tourism/TourismComparisonView';
import TourismRecommendations from '../../components/tourism/TourismRecommendations';
import TourismCertifications from '../../components/tourism/TourismCertifications';
import TourismCarbonCalculatorEngine from '../../utils/tourismCarbonCalculator';
import TourismStorageManager from '../../utils/tourismStorageManager';
import '../../styles/TourismCarbon.css';

const StyledContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(3),
  minHeight: '100vh',
  backgroundColor: '#f5f7fa',
}));

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 12px 48px rgba(0, 0, 0, 0.15)',
  },
}));

const HeaderCard = styled(StyledCard)(({ theme }) => ({
  background: 'linear-gradient(135deg, #2e7d32 0%, #43a047 100%)',
  color: 'white',
  marginBottom: theme.spacing(4),
}));

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tourism-tabpanel-${index}`}
      aria-labelledby={`tourism-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const TourismCarbonCalculatorPage = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [calculator] = useState(new TourismCarbonCalculatorEngine());
  const [currentCalculation, setCurrentCalculation] = useState(null);
  const [calculationHistory, setCalculationHistory] = useState([]);
  const [selectedComparisons, setSelectedComparisons] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = () => {
    try {
      const history = TourismStorageManager.getAllCalculations();
      const userSettings = TourismStorageManager.getSettings();
      
      setCalculationHistory(history);
      setSettings(userSettings);
    } catch (err) {
      setError('Veriler yüklenirken hata oluştu');
      console.error('Data loading error:', err);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setError(null);
    setSuccess(null);
  };

  const handleCalculation = async (inputData) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = calculator.calculateTotalFootprint(inputData);
      
      // Hesaplama sonucunu kaydet
      const calculationId = TourismStorageManager.saveCalculation({
        ...result,
        inputData,
        title: inputData.title || `Hesaplama - ${new Date().toLocaleDateString()}`,
        hotelName: inputData.hotelName || 'Tanımsız Otel'
      });

      result.id = calculationId;
      setCurrentCalculation(result);
      
      // Geçmişi güncelle
      const updatedHistory = TourismStorageManager.getAllCalculations();
      setCalculationHistory(updatedHistory);
      
      setSuccess('Hesaplama başarıyla tamamlandı!');
      setActiveTab(1); // Sonuçlar sekmesine geç
      
    } catch (err) {
      setError('Hesaplama sırasında hata oluştu: ' + err.message);
      console.error('Calculation error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCalculation = (calculationId) => {
    try {
      TourismStorageManager.deleteCalculation(calculationId);
      const updatedHistory = TourismStorageManager.getAllCalculations();
      setCalculationHistory(updatedHistory);
      
      // Eğer silinen hesaplama aktif hesaplama ise temizle
      if (currentCalculation && currentCalculation.id === calculationId) {
        setCurrentCalculation(null);
      }
      
      // Karşılaştırma listesinden de kaldır
      setSelectedComparisons(prev => 
        prev.filter(comp => comp.id !== calculationId)
      );
      
      setSuccess('Hesaplama silindi');
    } catch (err) {
      setError('Hesaplama silinirken hata oluştu');
      console.error('Delete error:', err);
    }
  };

  const handleLoadCalculation = (calculation) => {
    setCurrentCalculation(calculation);
    setActiveTab(1); // Sonuçlar sekmesine geç
  };

  const handleAddToComparison = (calculation) => {
    if (selectedComparisons.length >= 5) {
      setError('En fazla 5 hesaplama karşılaştırılabilir');
      return;
    }

    if (selectedComparisons.find(comp => comp.id === calculation.id)) {
      setError('Bu hesaplama zaten karşılaştırma listesinde');
      return;
    }

    setSelectedComparisons(prev => [...prev, calculation]);
    setSuccess('Hesaplama karşılaştırma listesine eklendi');
  };

  const handleRemoveFromComparison = (calculationId) => {
    setSelectedComparisons(prev => 
      prev.filter(comp => comp.id !== calculationId)
    );
  };

  const handleExportCalculations = async (calculationIds, format) => {
    try {
      setLoading(true);
      
      if (format === 'csv' || format === 'pdf') {
        // Export utility kullanılacak
        const { exportToCSV, exportToPDF } = await import('../../utils/tourismExportUtils');
        
        const calculations = calculationIds.map(id => 
          TourismStorageManager.getCalculationById(id)
        ).filter(Boolean);

        if (format === 'csv') {
          await exportToCSV(calculations);
        } else {
          await exportToPDF(calculations);
        }
        
        setSuccess(`Hesaplamalar ${format.toUpperCase()} formatında dışa aktarıldı`);
      }
    } catch (err) {
      setError('Dışa aktarma sırasında hata oluştu');
      console.error('Export error:', err);
    } finally {
      setLoading(false);
    }
  };

  const tabsData = [
    { label: 'Yeni Hesaplama', icon: '🧮' },
    { label: 'Sonuçlar', icon: '📊' },
    { label: 'Geçmiş', icon: '📋' },
    { label: 'Karşılaştırma', icon: '⚖️' },
    { label: 'Öneriler', icon: '💡' },
    { label: 'Sertifikasyon', icon: '🏆' }
  ];

  return (
    <StyledContainer maxWidth="xl">
      {/* Header */}
      <HeaderCard>
        <CardContent>
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            🌱 Otel & Turizm Karbon Ayak İzi Hesaplayıcısı
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9 }}>
            Sürdürülebilir turizm için kapsamlı karbon emisyon analizi ve yönetimi
          </Typography>
        </CardContent>
      </HeaderCard>

      {/* Alert Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Main Navigation Tabs */}
      <StyledCard>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="turizm karbon hesaplama sekmeleri"
          >
            {tabsData.map((tab, index) => (
              <Tab 
                key={index}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{tab.icon}</span>
                    <span>{tab.label}</span>
                  </Box>
                }
                id={`tourism-tab-${index}`}
                aria-controls={`tourism-tabpanel-${index}`}
              />
            ))}
          </Tabs>
        </Box>

        {/* Tab Panels */}
        <TabPanel value={activeTab} index={0}>
          <TourismInputForm
            onCalculate={handleCalculation}
            loading={loading}
            settings={settings}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          {currentCalculation ? (
            <TourismCalculationResults
              calculation={currentCalculation}
              onAddToComparison={handleAddToComparison}
              onExport={handleExportCalculations}
              calculator={calculator}
            />
          ) : (
            <Box textAlign="center" py={8}>
              <Typography variant="h6" color="textSecondary">
                Henüz hesaplama yapılmadı
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Yeni hesaplama sekmesinden başlayabilirsiniz
              </Typography>
              <Button 
                variant="contained" 
                onClick={() => setActiveTab(0)}
                sx={{ mt: 2 }}
              >
                Yeni Hesaplama Yap
              </Button>
            </Box>
          )}
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <TourismCalculationHistory
            calculations={calculationHistory}
            onLoad={handleLoadCalculation}
            onDelete={handleDeleteCalculation}
            onAddToComparison={handleAddToComparison}
            onExport={handleExportCalculations}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          <TourismComparisonView
            calculations={selectedComparisons}
            onRemove={handleRemoveFromComparison}
            onExport={handleExportCalculations}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={4}>
          <TourismRecommendations
            calculation={currentCalculation}
            history={calculationHistory}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={5}>
          <TourismCertifications
            calculation={currentCalculation}
            calculator={calculator}
          />
        </TabPanel>
      </StyledCard>

      {/* Quick Stats Footer */}
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent textAlign="center">
              <Typography variant="h4" color="primary" gutterBottom>
                {calculationHistory.length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Toplam Hesaplama
              </Typography>
            </CardContent>
          </StyledCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent textAlign="center">
              <Typography variant="h4" color="success.main" gutterBottom>
                {selectedComparisons.length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Karşılaştırma Listesi
              </Typography>
            </CardContent>
          </StyledCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent textAlign="center">
              <Typography variant="h4" color="warning.main" gutterBottom>
                {currentCalculation ? 
                  Math.round(currentCalculation.total) : 0}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Son Hesaplama (kg CO₂e)
              </Typography>
            </CardContent>
          </StyledCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent textAlign="center">
              <Typography variant="h4" color="info.main" gutterBottom>
                {currentCalculation ? 
                  Math.round(currentCalculation.perRoomNight) : 0}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Oda-Gece Başına (kg CO₂e)
              </Typography>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>
    </StyledContainer>
  );
};

export default TourismCarbonCalculatorPage;
